import {
  GetSecurityPipelineScheduledWorkflowsDocument,
  SecurityPipelineScheduledWorkflowCadence,
  useCreateSecurityPipelineScheduledWorkflowMutation,
  useGetSecurityPipelineScheduledWorkflowsQuery,
  useUpdateSecurityPipelineScheduledWorkflowMutation,
  useGetClientIntegrationQuery,
  GetClientIntegrationDocument,
  useUpdateClientIntegrationConfigurationMutation,
  ClientIntegrationType,
  useDeleteSecurityPipelineScheduledWorkflowMutation,
  useTriggerSecurityPipelineScheduledWorkflowMutation,
} from "~/operations";
import { useScope } from "~/hooks/useScope";
import { useParams } from "react-router-dom";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import {
  AUTOMATION_SCOPE,
  DEFAULT_FORM_VALUES,
  isValidCadence,
  CADENCE_OPTIONS,
  DAYS_OF_WEEK_OPTIONS,
  DAYS_OF_MONTH_OPTIONS,
} from "../constants";
import { SecurityPipelineGithubAutomateFormValues } from "../types";
import { useEffect, useMemo } from "react";
import { isWorkspaceMrn } from "~/lib/mrn";
import { useSnackbar } from "notistack";
import { getError } from "~/lib/handle-error";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export const useSecurityPipelineGithubAutomate = () => {
  const { activeScope } = useScope();
  const { enqueueSnackbar } = useSnackbar();
  if (!activeScope) {
    throw new Error("Active scope not found");
  }
  const { integrationId } = useParams();
  const integrationMrn = getIntegrationMrn({
    scope: activeScope,
    integrationId,
  });
  const formMethods = useForm<SecurityPipelineGithubAutomateFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const watchCadence = useWatch({
    control: formMethods.control,
    name: "cadence",
  });

  const watchScope = useWatch({
    control: formMethods.control,
    name: "scope",
  });

  const watchWorkspaceMrns = useWatch({
    control: formMethods.control,
    name: "workspaceMrns",
  });

  const onSubmit: SubmitHandler<
    SecurityPipelineGithubAutomateFormValues
  > = async (data: SecurityPipelineGithubAutomateFormValues) => {
    const { cadence, day, workspaceMrns, scope } = data;

    try {
      // If cadence is "NEVER", delete all existing schedules
      if (!cadence) {
        await handleDeleteAllSchedules();
        enqueueSnackbar("All schedules deleted successfully!", {
          variant: "success",
        });
        return;
      }

      // Type guard: at this point cadence must be valid (checked above)
      if (!isValidCadence(cadence)) {
        throw new Error("Invalid cadence value");
      }

      // Validate scope and workspace selection
      let scopeMrns: string[];
      if (scope === AUTOMATION_SCOPE.SPACE) {
        if (!activeScope?.mrn) {
          throw new Error(
            "Active scope MRN is required for space-level scheduling",
          );
        }
        scopeMrns = [activeScope.mrn];
      } else if (scope === AUTOMATION_SCOPE.WORKSPACE) {
        if (!workspaceMrns || workspaceMrns.length === 0) {
          enqueueSnackbar("Please select at least one workspace", {
            variant: "warning",
          });
          return;
        }
        scopeMrns = workspaceMrns;
      } else {
        throw new Error(`Invalid scope: ${scope}`);
      }

      if (!hasScheduledWorkflows) {
        // No existing schedules - create new ones based on scope
        if (scope === AUTOMATION_SCOPE.SPACE) {
          await handleCreateSpaceSchedule(cadence, day);
        } else {
          await handleCreateWorkspaceSchedules(workspaceMrns, cadence, day);
        }
      } else {
        // Handle existing schedules based on scope changes
        const currentScope = isWorkspaceMrn(
          firstScheduledWorkflow?.selection.scopeMrn ?? "",
        )
          ? AUTOMATION_SCOPE.WORKSPACE
          : AUTOMATION_SCOPE.SPACE;

        // If switching to SPACE scope, delete all existing schedules first
        if (
          scope === AUTOMATION_SCOPE.SPACE &&
          currentScope === AUTOMATION_SCOPE.WORKSPACE
        ) {
          await handleSwitchToSpaceScope(cadence, day);
        } else if (
          scope === AUTOMATION_SCOPE.SPACE &&
          currentScope === AUTOMATION_SCOPE.SPACE
        ) {
          // Already space-level, just update the existing schedule
          await updateSecurityPipelineScheduledWorkflow({
            variables: {
              input: {
                configuration: {
                  cadence,
                  ...(day && { day }),
                },
                selection: {
                  scopeMrn: activeScope?.mrn ?? "",
                },
                mrn: firstScheduledWorkflow?.mrn ?? "",
              },
            },
            refetchQueries: [refetchQueryConfig],
            awaitRefetchQueries: true,
          });
        } else {
          // WORKSPACE scope - handle workspace-specific schedules
          await handleWorkspaceScheduleUpdates(scopeMrns, cadence, day);
        }
      }
      enqueueSnackbar("Schedule updated successfully!", {
        variant: "success",
      });
    } catch (error) {
      const err = getError(error);
      enqueueSnackbar(`Error managing scheduled workflows: ${err}`, {
        variant: "error",
      });
    }
  };

  const [updateIntegration, { loading: updateIntegrationLoading }] =
    useUpdateClientIntegrationConfigurationMutation({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        ...(integrationId
          ? [
              {
                query: GetClientIntegrationDocument,
                variables: {
                  mrn: getIntegrationMrn({
                    scope: activeScope,
                    integrationId: integrationId || "",
                  }),
                },
              },
            ]
          : []),
      ],
    });

  const { data: clientIntegrationData, loading: clientIntegrationLoading } =
    useGetClientIntegrationQuery({
      notifyOnNetworkStatusChange: true,
      variables: { mrn: integrationMrn || "skip" },
      skip: !integrationMrn || !integrationId,
      fetchPolicy: "network-only",
    });

  const integration = clientIntegrationData?.clientIntegration?.integration;

  const isAutoCreateWorkflows =
    integration?.configurationOptions?.__typename ===
    "SecurityPipelineGithubConfigurationOptions"
      ? integration.configurationOptions.autoCreateWorkflows
      : false;

  const [
    createSecurityPipelineScheduledWorkflow,
    {
      loading: createSecurityPipelineScheduledWorkflowLoading,
      error: createSecurityPipelineScheduledWorkflowError,
    },
  ] = useCreateSecurityPipelineScheduledWorkflowMutation({
    notifyOnNetworkStatusChange: true,
  });

  const [
    updateSecurityPipelineScheduledWorkflow,
    {
      loading: updateSecurityPipelineScheduledWorkflowLoading,
      error: updateSecurityPipelineScheduledWorkflowError,
    },
  ] = useUpdateSecurityPipelineScheduledWorkflowMutation({
    notifyOnNetworkStatusChange: true,
  });

  const [
    deleteSecurityPipelineScheduledWorkflow,
    {
      loading: deleteSecurityPipelineScheduledWorkflowLoading,
      error: deleteSecurityPipelineScheduledWorkflowError,
    },
  ] = useDeleteSecurityPipelineScheduledWorkflowMutation({
    notifyOnNetworkStatusChange: true,
  });

  const [
    triggerSecurityPipelineScheduledWorkflow,
    {
      loading: triggerSecurityPipelineScheduledWorkflowLoading,
      error: triggerSecurityPipelineScheduledWorkflowError,
    },
  ] = useTriggerSecurityPipelineScheduledWorkflowMutation({
    notifyOnNetworkStatusChange: true,
  });

  const {
    data,
    loading: getSecurityPipelineScheduledWorkflowsLoading,
    error: getSecurityPipelineScheduledWorkflowsError,
    refetch: refetchScheduledWorkflows,
  } = useGetSecurityPipelineScheduledWorkflowsQuery({
    variables: {
      input: {
        scopeMrn: activeScope?.mrn ?? "",
      },
    },
  });

  // Memoize refetch query config to ensure consistent variable matching
  const refetchQueryConfig = useMemo(
    () => ({
      query: GetSecurityPipelineScheduledWorkflowsDocument,
      variables: {
        input: {
          scopeMrn: activeScope?.mrn ?? "",
        },
      },
    }),
    [activeScope?.mrn],
  );

  // Memoize scheduledWorkflows to avoid creating new array reference on every render
  const scheduledWorkflows = useMemo(() => {
    return data?.securityPipelineScheduledWorkflows.__typename ===
      "SecurityPipelineScheduledWorkflowConnection"
      ? (data.securityPipelineScheduledWorkflows?.edges?.map(
          (edge) => edge.node,
        ) ?? [])
      : [];
  }, [data]);

  const hasScheduledWorkflows = scheduledWorkflows.length > 0;

  const isDisableUpdateButton =
    (!watchCadence && !hasScheduledWorkflows) ||
    (!hasScheduledWorkflows &&
      watchScope === AUTOMATION_SCOPE.WORKSPACE &&
      watchWorkspaceMrns.length === 0);

  const firstScheduledWorkflow = scheduledWorkflows.at(0);

  // Memoize workspace MRNs to avoid creating new array reference on every render
  const workspaceMrnsFromWorkflows = useMemo(() => {
    if (
      !firstScheduledWorkflow ||
      !isWorkspaceMrn(firstScheduledWorkflow.selection.scopeMrn ?? "")
    ) {
      return [];
    }
    return scheduledWorkflows.map((workflow) => workflow.selection.scopeMrn);
  }, [firstScheduledWorkflow, scheduledWorkflows]);

  useEffect(() => {
    formMethods.reset({
      ...DEFAULT_FORM_VALUES,
      autoCreateWorkflows: isAutoCreateWorkflows,
      ...(firstScheduledWorkflow && {
        cadence: firstScheduledWorkflow?.configuration.cadence,
        day: firstScheduledWorkflow?.configuration.day ?? 0,
        scope: isWorkspaceMrn(firstScheduledWorkflow?.selection.scopeMrn ?? "")
          ? AUTOMATION_SCOPE.WORKSPACE
          : AUTOMATION_SCOPE.SPACE,
        workspaceMrns: workspaceMrnsFromWorkflows,
      }),
    });
  }, [
    isAutoCreateWorkflows,
    firstScheduledWorkflow,
    workspaceMrnsFromWorkflows,
    formMethods,
  ]);

  // Helper function to create workflow input structure
  const createWorkflowInput = (
    scopeMrn: string,
    cadence: SecurityPipelineScheduledWorkflowCadence,
    day?: number,
  ) => ({
    configuration: {
      cadence,
      ...(day && { day }),
    },
    ownerMrn: activeScope?.mrn ?? "",
    integrationMrn: integrationMrn,
    selection: { scopeMrn },
    title: "Security Pipeline Scheduled Workflow",
  });

  // Helper function to delete all existing schedules
  const handleDeleteAllSchedules = async () => {
    if (scheduledWorkflows.length > 0) {
      // For parallel deletes, only refetch once after all mutations complete
      await Promise.all(
        scheduledWorkflows.map((workflow) => {
          return deleteSecurityPipelineScheduledWorkflow({
            variables: {
              mrns: [workflow.mrn],
            },
            // Don't refetch on individual calls - we'll refetch after all complete
            refetchQueries: [],
          });
        }),
      );
      // Single refetch after all deletes complete
      await refetchScheduledWorkflows();
    }
  };

  // Helper function to create space-level schedule
  const handleCreateSpaceSchedule = async (
    cadence: SecurityPipelineScheduledWorkflowCadence,
    day?: number,
  ) => {
    await createSecurityPipelineScheduledWorkflow({
      variables: {
        input: createWorkflowInput(activeScope?.mrn ?? "", cadence, day),
      },
      refetchQueries: [refetchQueryConfig],
      awaitRefetchQueries: true,
    });
  };

  // Helper function to create workspace-level schedules
  const handleCreateWorkspaceSchedules = async (
    workspaceMrns: string[],
    cadence: SecurityPipelineScheduledWorkflowCadence,
    day?: number,
  ) => {
    // For parallel creates, only refetch once after all mutations complete
    // to avoid race conditions and duplicate refetch requests
    await Promise.all(
      workspaceMrns.map((scopeMrn: string) => {
        return createSecurityPipelineScheduledWorkflow({
          variables: {
            input: createWorkflowInput(scopeMrn, cadence, day),
          },
          // Don't refetch on individual calls - we'll refetch after all complete
          refetchQueries: [],
        });
      }),
    );
    // Single refetch after all creates complete
    await refetchScheduledWorkflows();
  };

  // Helper function to handle switching from WORKSPACE to SPACE scope
  const handleSwitchToSpaceScope = async (
    cadence: SecurityPipelineScheduledWorkflowCadence,
    day?: number,
  ) => {
    // Delete all workspace schedules
    if (scheduledWorkflows.length > 0) {
      await Promise.all(
        scheduledWorkflows.map((workflow) => {
          return deleteSecurityPipelineScheduledWorkflow({
            variables: {
              mrns: [workflow.mrn],
            },
            refetchQueries: [],
          });
        }),
      );
    }

    // Create space-level schedule (this will refetch on its own)
    await handleCreateSpaceSchedule(cadence, day);
  };

  // Helper function to handle workspace schedule updates (create, update, delete)
  const handleWorkspaceScheduleUpdates = async (
    scopeMrns: string[],
    cadence: SecurityPipelineScheduledWorkflowCadence,
    day?: number,
  ) => {
    const newScheduledWorkflows = scopeMrns.filter(
      (scopeMrn: string) =>
        !scheduledWorkflows.some(
          (workflow) => workflow.selection.scopeMrn === scopeMrn,
        ),
    );
    const scheduledWorkflowsToDelete = scheduledWorkflows.filter(
      (workflow) => !scopeMrns.includes(workflow.selection.scopeMrn),
    );
    const scheduledWorkflowsToUpdate = scheduledWorkflows.filter((workflow) =>
      scopeMrns.includes(workflow.selection.scopeMrn),
    );

    // Delete removed workspace schedules first (must complete before creates)
    if (scheduledWorkflowsToDelete.length > 0) {
      await Promise.all(
        scheduledWorkflowsToDelete.map((workflow) => {
          return deleteSecurityPipelineScheduledWorkflow({
            variables: {
              mrns: [workflow.mrn],
            },
            // Don't refetch on individual calls - we'll refetch after all operations complete
            refetchQueries: [],
          });
        }),
      );
    }

    // Update existing workspace schedules (can run in parallel)
    if (scheduledWorkflowsToUpdate.length > 0) {
      await Promise.all(
        scheduledWorkflowsToUpdate.map((workflow) => {
          return updateSecurityPipelineScheduledWorkflow({
            variables: {
              input: {
                configuration: {
                  cadence,
                  ...(day && { day }),
                },
                selection: {
                  scopeMrn: workflow.selection.scopeMrn,
                },
                mrn: workflow.mrn,
              },
            },
            // Don't refetch on individual calls - we'll refetch after all operations complete
            refetchQueries: [],
          });
        }),
      );
    }

    // Create new workspace schedules (must happen after deletes complete)
    if (newScheduledWorkflows.length > 0) {
      await Promise.all(
        newScheduledWorkflows.map((scopeMrn: string) => {
          return createSecurityPipelineScheduledWorkflow({
            variables: {
              input: createWorkflowInput(scopeMrn, cadence, day),
            },
            // Don't refetch on individual calls - we'll refetch after all operations complete
            refetchQueries: [],
          });
        }),
      );
    }

    // Single refetch after all operations (delete, update, create) complete
    await refetchScheduledWorkflows();
  };

  const handleUpdateAutoCreateWorkflows = async (
    autoCreateWorkflows: boolean,
  ) => {
    await updateIntegration({
      variables: {
        input: {
          mrn: getIntegrationMrn({
            scope: activeScope,
            integrationId,
          }),
          type: ClientIntegrationType.SecurityPipelineGithub,
          configurationOptions: {
            securityPipelineGithubConfigurationOptions: {
              autoCreateWorkflows,
            },
          },
        },
      },
    });
  };

  const handleTriggerSecurityPipelineScheduledWorkflows = async () => {
    try {
      await Promise.all(
        scheduledWorkflows.map((workflow) => {
          return triggerSecurityPipelineScheduledWorkflow({
            variables: {
              mrn: workflow.mrn,
            },
          });
        }),
      );
      enqueueSnackbar("Scheduled workflows triggered successfully!", {
        variant: "success",
      });
    } catch (error) {
      const err = getError(error);
      enqueueSnackbar(`Error triggering scheduled workflows: ${err}`, {
        variant: "error",
      });
    }
  };

  return {
    isAutoCreateWorkflows,
    handleUpdateAutoCreateWorkflows,
    handleTriggerSecurityPipelineScheduledWorkflows,
    cadenceOptions: CADENCE_OPTIONS,
    daysOfWeekOptions: DAYS_OF_WEEK_OPTIONS,
    daysOfMonthOptions: DAYS_OF_MONTH_OPTIONS,
    formMethods,
    onSubmit,
    isLoading:
      getSecurityPipelineScheduledWorkflowsLoading ||
      updateSecurityPipelineScheduledWorkflowLoading ||
      createSecurityPipelineScheduledWorkflowLoading ||
      deleteSecurityPipelineScheduledWorkflowLoading,
    isIntegrationLoading: clientIntegrationLoading || updateIntegrationLoading,
    isRunNowLoading: triggerSecurityPipelineScheduledWorkflowLoading,
    error:
      getSecurityPipelineScheduledWorkflowsError ||
      updateSecurityPipelineScheduledWorkflowError ||
      createSecurityPipelineScheduledWorkflowError ||
      deleteSecurityPipelineScheduledWorkflowError ||
      triggerSecurityPipelineScheduledWorkflowError,
    firstScheduledWorkflow,
    hasScheduledWorkflows: scheduledWorkflows.length > 0,
    isDisableUpdateButton,
    isCadenceSet: !!watchCadence,
  };
};
